import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import CustomPhoneField from './CustomPhoneField';
import {
  fetchGravityFormDraft,
  formatGravityFormPayload,
  saveGravityFormDraft,
} from '../../utils/gravityFormDraft';

const EMPTY_SNAPSHOT = '{}';

const isFileInstance = (value) =>
  typeof File !== 'undefined' && value instanceof File;

const isFileListInstance = (value) =>
  typeof FileList !== 'undefined' && value instanceof FileList;

const normalizeDirtyValue = (value) => {
  if (value === undefined || value === null || value === '') return undefined;

  if (value instanceof Date) return value.toISOString();

  if (isFileInstance(value)) {
    return {
      __type: 'file',
      name: value.name,
      size: value.size,
      type: value.type,
      lastModified: value.lastModified,
    };
  }

  if (isFileListInstance(value)) {
    const files = Array.from(value)
      .map((file) => normalizeDirtyValue(file))
      .filter((entry) => entry !== undefined);
    return files.length ? files : undefined;
  }

  if (Array.isArray(value)) {
    const normalizedArray = value
      .map((entry) => normalizeDirtyValue(entry))
      .filter((entry) => entry !== undefined);
    return normalizedArray.length ? normalizedArray : undefined;
  }

  if (typeof value === 'object') {
    const normalizedEntries = Object.entries(value)
      .map(([key, entryValue]) => [key, normalizeDirtyValue(entryValue)])
      .filter(([, entryValue]) => entryValue !== undefined)
      .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey));

    return normalizedEntries.length
      ? Object.fromEntries(normalizedEntries)
      : undefined;
  }

  return value;
};

const createDirtySnapshot = (values) =>
  JSON.stringify(normalizeDirtyValue(values) || {});

const FILE_SIZE_UNITS = ['B', 'KB', 'MB', 'GB'];

const formatFileSize = (bytes) => {
  if (!Number.isFinite(bytes) || bytes < 0) return '';

  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < FILE_SIZE_UNITS.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const precision = unitIndex === 0 || value >= 10 ? 0 : 1;
  return `${value.toFixed(precision)}${FILE_SIZE_UNITS[unitIndex]}`;
};

const GENERATED_FILE_PREVIEW_SELECTOR = '[data-generated-file-preview="true"]';
const SINGLE_FILE_INPUT_SELECTOR =
  '.gfield--type-fileupload .ginput_container_fileupload > input[type="file"]';

const buildSingleFileUploadPreview = (file, { onRemove }) => {
  const list = document.createElement('div');
  list.className = 'ginput_preview_list';
  list.setAttribute('data-generated-file-preview', 'true');

  const row = document.createElement('div');
  row.className = 'ginput_preview';

  const nameElement = document.createElement('span');
  nameElement.className = 'gfield_fileupload_filename';
  nameElement.textContent = `${file.name} (${formatFileSize(file.size)})`;

  const removeButton = document.createElement('button');
  removeButton.type = 'button';
  removeButton.className =
    'gform_delete_file gform-theme-button gform-theme-button--simple';
  removeButton.setAttribute('aria-label', `Remove ${file.name}`);
  removeButton.addEventListener('click', onRemove);

  const iconElement = document.createElement('span');
  iconElement.className = 'dashicons dashicons-trash';
  iconElement.setAttribute('aria-hidden', 'true');

  removeButton.appendChild(iconElement);
  row.appendChild(nameElement);
  row.appendChild(removeButton);
  list.appendChild(row);

  return list;
};

const SUBMIT_ERROR_MESSAGE =
  'Sorry, something went wrong and your form could not be submitted. Please check the form and try again.';

export const GRAVITY_FORM_HELPER_TEXT = {
  // Submit button text while the submission is in flight (library default is 'Loading...')
  loading: 'Submitting...',
  errors: {
    general: SUBMIT_ERROR_MESSAGE,
    unknownError: SUBMIT_ERROR_MESSAGE,
  },
};

const getAddressFieldDefaults = (fields = []) =>
  fields
    .filter((field) => field?.type === 'ADDRESS')
    .map((field) => ({
      name: `input_${field.id}`,
      country: field.defaultCountry || 'United States',
    }));

const mergeAddressCountryPresetValues = (presetValues, addressDefaults) => {
  if (!Array.isArray(addressDefaults) || addressDefaults.length === 0) {
    return presetValues || undefined;
  }

  const mergedValues = {
    ...(presetValues && typeof presetValues === 'object' ? presetValues : {}),
  };

  addressDefaults.forEach(({ name, country }) => {
    const currentValue = mergedValues[name];
    const currentAddress =
      currentValue &&
      typeof currentValue === 'object' &&
      !Array.isArray(currentValue)
        ? currentValue
        : {};

    mergedValues[name] = {
      ...currentAddress,
      country: currentAddress.country || country,
    };
  });

  return mergedValues;
};

const ensureAddressCountries = (formInstance, addressDefaults) => {
  if (!formInstance || typeof formInstance.setValue !== 'function') return;
  if (!Array.isArray(addressDefaults) || addressDefaults.length === 0) return;

  const values =
    typeof formInstance.getValues === 'function'
      ? formInstance.getValues()
      : {};

  addressDefaults.forEach(({ name, country }) => {
    const currentValue = values?.[name];
    const currentAddress =
      currentValue &&
      typeof currentValue === 'object' &&
      !Array.isArray(currentValue)
        ? currentValue
        : {};

    if (currentAddress.country) return;

    formInstance.setValue(
      name,
      {
        ...currentAddress,
        country,
      },
      {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      }
    );
  });
};

export default function useGravityForm({ block, gravityForms }) {
  const router = useRouter();
  const queryToken = router.query?.gf_token;
  const resumeToken = Array.isArray(queryToken) ? queryToken[0] : queryToken;
  const normalizedToken =
    typeof resumeToken === 'string' ? resumeToken.trim() : '';
  const hasResumeToken = Boolean(normalizedToken);
  const formRef = useRef(null);
  const lastRestoredTokenRef = useRef('');
  const restoringTokenRef = useRef('');
  const initialSnapshotRef = useRef(EMPTY_SNAPSHOT);
  const hasUnsavedChangesRef = useRef(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [draftError, setDraftError] = useState('');
  const [draftResumeUrl, setDraftResumeUrl] = useState('');
  const [draftMessage, setDraftMessage] = useState('');
  const [isRestoringDraft, setIsRestoringDraft] = useState(false);
  const [draftRestoreError, setDraftRestoreError] = useState('');
  const [draftFieldValues, setDraftFieldValues] = useState(null);
  const [hasAppliedDraftValues, setHasAppliedDraftValues] = useState(false);
  const [isFormReady, setIsFormReady] = useState(false);
  const [activeResumeToken, setActiveResumeToken] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [hasSubmittedForm, setHasSubmittedForm] = useState(false);
  const lastKnownPageRef = useRef(1);
  const lastPageForScrollRef = useRef(1);
  const hasInitializedPageScrollRef = useRef(false);

  const formId = parseInt(block?.gravityFormId, 10);
  const hasValidFormId = Number.isInteger(formId) && formId > 0;
  const formData = hasValidFormId
    ? gravityForms?.[String(formId)] || gravityForms?.[formId]
    : null;
  const hasValidConfig = Boolean(block && gravityForms && formData?.gfForm);

  const saveAndContinueSettings = formData?.gfForm?.saveAndContinue;
  const hasSaveAndContinue = Boolean(
    saveAndContinueSettings?.hasSaveAndContinue
  );
  const graphFormId = formData?.gfForm?.databaseId || formId;
  const hasRequiredFields = useMemo(() => {
    const formFields = formData?.gfForm?.formFields?.nodes || [];
    return formFields.some((field) => field?.isRequired);
  }, [formData]);
  const addressDefaults = useMemo(
    () => getAddressFieldDefaults(formData?.gfForm?.formFields?.nodes || []),
    [formData]
  );
  const presetValues = useMemo(() => {
    const nextPresetValues = mergeAddressCountryPresetValues(
      hasResumeToken ? draftFieldValues || undefined : undefined,
      addressDefaults
    );

    if (addressDefaults.length > 0) {
      nextPresetValues.country = nextPresetValues.country || 'United States';
    }

    return nextPresetValues;
  }, [addressDefaults, draftFieldValues, hasResumeToken]);
  const customFormFields = useMemo(() => {
    const formFields = formData?.gfForm?.formFields?.nodes || [];

    return formFields.reduce((overrides, field) => {
      if (field?.type === 'PHONE') {
        overrides[field.id] = CustomPhoneField;
      }
      return overrides;
    }, {});
  }, [formData]);

  const totalPages = useMemo(() => {
    const formFields = formData?.gfForm?.formFields?.nodes || [];
    const pageNumbers = formFields
      .map((field) => Number(field?.pageNumber))
      .filter((pageNumber) => Number.isInteger(pageNumber) && pageNumber > 0);

    return pageNumbers.length ? Math.max(...pageNumbers) : 1;
  }, [formData]);
  const progressPercentage =
    totalPages > 1 ? Math.round((currentPage / totalPages) * 100) : 0;
  const hasDraftPresetValues = draftFieldValues !== null;
  const shouldHoldFormRenderForRestore =
    hasResumeToken &&
    !draftRestoreError &&
    !hasDraftPresetValues &&
    (isRestoringDraft || lastRestoredTokenRef.current !== normalizedToken);
  const hasRestoredDraft = Boolean(
    hasResumeToken &&
    !draftRestoreError &&
    hasAppliedDraftValues &&
    lastRestoredTokenRef.current === normalizedToken
  );
  const formRenderKey = hasResumeToken
    ? `gravity-form-${graphFormId}-token-${normalizedToken}-${
        hasDraftPresetValues ? 'draft' : 'base'
      }`
    : `gravity-form-${graphFormId}`;

  useEffect(() => {
    if (!isFormReady) return;
    if (!formRef.current) return;

    ensureAddressCountries(formRef.current, addressDefaults);

    if (typeof formRef.current.subscribeToAllValues !== 'function')
      return undefined;

    const unsubscribe = formRef.current.subscribeToAllValues(() => {
      ensureAddressCountries(formRef.current, addressDefaults);
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [addressDefaults, formRenderKey, isFormReady]);
  const handleFormValidationCapture = useCallback(() => {
    ensureAddressCountries(formRef.current, addressDefaults);
  }, [addressDefaults]);
  const setFormInstance = useCallback((instance) => {
    formRef.current = instance;
    const nextIsFormReady = Boolean(instance);
    startTransition(() => {
      setIsFormReady((previousValue) =>
        previousValue === nextIsFormReady ? previousValue : nextIsFormReady
      );
    });
  }, []);

  const setUnsavedChangesFlag = useCallback((isDirty) => {
    if (hasUnsavedChangesRef.current === isDirty) return;
    hasUnsavedChangesRef.current = isDirty;
    setHasUnsavedChanges(isDirty);
  }, []);

  const scrollFormWrapperToTop = useCallback(
    ({ smooth = true } = {}) => {
      if (typeof window === 'undefined') return;

      const formWrapper = document.getElementById(
        `gform_wrapper_${graphFormId}`
      );
      if (!formWrapper) return;

      const prefersReducedMotion = window.matchMedia?.(
        '(prefers-reduced-motion: reduce)'
      )?.matches;

      formWrapper.scrollIntoView({
        block: 'start',
        behavior: smooth && !prefersReducedMotion ? 'smooth' : 'auto',
      });
    },
    [graphFormId]
  );

  const markCurrentFormValuesAsSaved = useCallback(
    (valuesOverride) => {
      const values =
        valuesOverride ||
        (formRef.current?.getValues ? formRef.current.getValues() : {});
      initialSnapshotRef.current = createDirtySnapshot(values);
      setUnsavedChangesFlag(false);
    },
    [setUnsavedChangesFlag]
  );

  const applyDraftValues = useCallback(() => {
    if (!draftFieldValues) return;
    if (!formRef.current) return;

    const getValuesFn =
      typeof formRef.current.getValues === 'function'
        ? formRef.current.getValues
        : null;
    const setValueFn =
      typeof formRef.current.setValue === 'function'
        ? formRef.current.setValue
        : null;
    const resetFn =
      typeof formRef.current.reset === 'function'
        ? formRef.current.reset
        : null;

    const valuesToApply = {};

    Object.entries(draftFieldValues).forEach(([fieldName, draftValue]) => {
      if (draftValue === undefined) return;
      valuesToApply[fieldName] = draftValue;
    });

    if (Object.keys(valuesToApply).length === 0) return;

    if (setValueFn) {
      Object.entries(valuesToApply).forEach(([fieldName, draftValue]) => {
        setValueFn(fieldName, draftValue, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      });
      return;
    }

    if (!resetFn) return;

    const currentValues = getValuesFn ? getValuesFn() || {} : {};
    resetFn(
      {
        ...currentValues,
        ...valuesToApply,
      },
      {
        keepErrors: true,
        keepTouched: true,
        keepIsSubmitted: true,
        keepSubmitCount: true,
      }
    );
  }, [draftFieldValues]);

  const syncCurrentPage = useCallback(
    (formElement) => {
      if (totalPages <= 1) {
        setCurrentPage(1);
        return;
      }
      if (!formElement) return;

      const currentPageElement = formElement.querySelector('.gform_page');
      const pageMatch = currentPageElement?.id?.match(/gform_page_\d+_(\d+)/);
      const parsedPage = pageMatch ? parseInt(pageMatch[1], 10) : 1;
      const safePage =
        Number.isFinite(parsedPage) &&
        parsedPage >= 1 &&
        parsedPage <= totalPages
          ? parsedPage
          : 1;

      startTransition(() => {
        setCurrentPage((previousPage) =>
          previousPage === safePage ? previousPage : safePage
        );
      });
    },
    [totalPages]
  );

  const syncDraftFileUploadNotes = useCallback(
    (formElement) => {
      if (!formElement) return;

      const fileUploadFields = formElement.querySelectorAll(
        '.gfield--type-fileupload'
      );
      if (!fileUploadFields.length) return;

      fileUploadFields.forEach((fieldElement) => {
        const existingNote = fieldElement.querySelector(
          '.BlockGravityForm-fileRestoreNote'
        );

        if (!hasRestoredDraft) {
          if (existingNote) {
            existingNote.remove();
          }
          return;
        }

        if (existingNote) return;

        const noteElement = document.createElement('p');
        noteElement.className =
          'gfield_description BlockGravityForm-fileRestoreNote';
        noteElement.textContent =
          "Files can't be restored for security reasons. Please re-upload any required files before submitting.";

        fieldElement.appendChild(noteElement);
      });
    },
    [hasRestoredDraft]
  );

  useEffect(() => {
    if (!isFormReady) return;
    if (!formRef.current) return;
    if (typeof formRef.current.subscribeToAllValues !== 'function') return;

    const baselineValues = (() => {
      const currentValues = formRef.current.getValues
        ? formRef.current.getValues()
        : {};
      if (hasResumeToken && draftFieldValues) {
        return { ...draftFieldValues, ...currentValues };
      }
      return currentValues;
    })();

    markCurrentFormValuesAsSaved(baselineValues);

    const unsubscribe = formRef.current.subscribeToAllValues((values) => {
      const currentSnapshot = createDirtySnapshot(values);
      setUnsavedChangesFlag(currentSnapshot !== initialSnapshotRef.current);
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [
    draftFieldValues,
    formRenderKey,
    hasResumeToken,
    isFormReady,
    markCurrentFormValuesAsSaved,
    setUnsavedChangesFlag,
  ]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!hasUnsavedChanges) return;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = true;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (!router.isReady) return;
    if (!formData) return;

    if (!normalizedToken) return;
    if (lastRestoredTokenRef.current === normalizedToken) return;
    if (restoringTokenRef.current === normalizedToken) return;

    restoringTokenRef.current = normalizedToken;

    let cancelled = false;

    const restoreDraftValues = async () => {
      startTransition(() => {
        setIsRestoringDraft(true);
        setDraftRestoreError('');
        setDraftFieldValues(null);
        setHasAppliedDraftValues(false);
      });

      try {
        const response = await fetchGravityFormDraft({
          resumeToken: normalizedToken,
        });

        if (cancelled) return;

        const responseErrors = response?.errors || [];
        if (responseErrors.length) {
          setDraftRestoreError(
            'Could not restore your saved progress right now. Please try again.'
          );

          if (process.env.NODE_ENV !== 'production') {
            console.error(
              'Restore draft returned GraphQL errors:',
              responseErrors
            );
          }
          return;
        }

        const draftEntry = response?.draftEntry;
        if (!draftEntry) {
          setDraftRestoreError(
            'This save link is invalid or has expired. Please start a new form.'
          );
          setActiveResumeToken('');
          return;
        }
        if (draftEntry.isSubmitted) {
          setDraftRestoreError(
            'This save link has already been submitted and can no longer be resumed.'
          );
          setActiveResumeToken('');
          return;
        }

        if (
          draftEntry.formDatabaseId &&
          graphFormId &&
          Number(draftEntry.formDatabaseId) !== Number(graphFormId)
        ) {
          setDraftRestoreError(
            'This save link belongs to a different form and cannot be restored here.'
          );
          setActiveResumeToken('');
          return;
        }

        setDraftFieldValues(response?.fieldValues || {});
        setActiveResumeToken(draftEntry.resumeToken || normalizedToken);
        lastRestoredTokenRef.current = normalizedToken;
      } catch (error) {
        if (cancelled) return;

        setDraftRestoreError(
          'Could not restore your saved progress right now. Please try again.'
        );

        if (process.env.NODE_ENV !== 'production') {
          console.error('Restore draft request error:', error);
        }
      } finally {
        if (!cancelled) {
          startTransition(() => {
            setIsRestoringDraft(false);
          });
        }
        restoringTokenRef.current = '';
      }
    };

    restoreDraftValues();

    return () => {
      cancelled = true;
    };
  }, [formData, graphFormId, normalizedToken, router.isReady]);

  useEffect(() => {
    if (!draftFieldValues || hasAppliedDraftValues) return;
    if (!isFormReady) return;
    if (!formRef.current) return;
    applyDraftValues();
    setHasAppliedDraftValues(true);
  }, [applyDraftValues, draftFieldValues, hasAppliedDraftValues, isFormReady]);

  useEffect(() => {
    startTransition(() => {
      setCurrentPage(1);
    });
  }, [graphFormId, normalizedToken]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const previousPage = lastPageForScrollRef.current;
    lastPageForScrollRef.current = currentPage;

    if (!hasInitializedPageScrollRef.current) {
      hasInitializedPageScrollRef.current = true;
      return;
    }

    // Only scroll when moving forward with "Next".
    if (currentPage <= previousPage) return;

    scrollFormWrapperToTop({ smooth: true });
  }, [currentPage, scrollFormWrapperToTop]);

  useEffect(() => {
    const previousPage = lastKnownPageRef.current;
    lastKnownPageRef.current = currentPage;

    if (currentPage === previousPage) return;
    if (!draftResumeUrl) return;
    setDraftResumeUrl('');
    setDraftMessage('');
  }, [currentPage, draftResumeUrl]);

  const syncFileUploadFileSizes = useCallback((formElement) => {
    if (!formElement) return;
    if (typeof formRef.current?.getValues !== 'function') return;

    const previewLists = formElement.querySelectorAll(
      '.gfield--type-fileupload .ginput_preview_list'
    );
    if (!previewLists.length) return;

    const values = formRef.current.getValues();

    previewLists.forEach((previewList) => {
      const fieldId = previewList.id?.match(/gform_preview_\d+_(\d+)/)?.[1];
      if (!fieldId) return;

      const fieldValue = values?.[`input_${fieldId}`];
      const files = Array.isArray(fieldValue)
        ? fieldValue
        : fieldValue
          ? [fieldValue]
          : [];
      if (!files.length) return;

      previewList.querySelectorAll('.ginput_preview').forEach((row, index) => {
        const file = files[index];
        if (!file || typeof file.size !== 'number' || !file.name) return;

        const filenameElement = row.querySelector(
          '.gfield_fileupload_filename'
        );
        if (!filenameElement) return;

        const label = `${file.name} (${formatFileSize(file.size)})`;
        if (filenameElement.textContent === label) return;

        filenameElement.textContent = label;
      });
    });
  }, []);

  const bindSingleFileUploadPreviews = useCallback((formElement) => {
    if (!formElement) return;

    formElement
      .querySelectorAll(SINGLE_FILE_INPUT_SELECTOR)
      .forEach((input) => {
        if (input.dataset.previewBound === 'true') return;
        input.dataset.previewBound = 'true';

        const fieldElement =
          input.closest('.gfield--type-fileupload') || input.parentElement;

        const removeExistingPreview = () => {
          const existing = fieldElement.nextElementSibling;
          if (existing?.matches(GENERATED_FILE_PREVIEW_SELECTOR)) {
            existing.remove();
          }
        };

        input.addEventListener('change', () => {
          removeExistingPreview();

          const file = input.files?.[0];
          if (!file) return;

          fieldElement.insertAdjacentElement(
            'afterend',
            buildSingleFileUploadPreview(file, {
              onRemove: () => {
                input.value = '';
                if (typeof formRef.current?.setValue === 'function') {
                  formRef.current.setValue(input.name, null, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }
                removeExistingPreview();
              },
            })
          );
        });
      });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const formElement = document.getElementById(`gform_${graphFormId}`);
    if (!formElement) return;

    let rafId = null;
    let timeoutId = null;
    const scheduleDraftSync = () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }

      rafId = window.requestAnimationFrame(() => {
        if (hasAppliedDraftValues && draftFieldValues) {
          applyDraftValues();
        }
        syncDraftFileUploadNotes(formElement);
        syncFileUploadFileSizes(formElement);
        bindSingleFileUploadPreviews(formElement);
        syncCurrentPage(formElement);
        ensureAddressCountries(formRef.current, addressDefaults);

        timeoutId = window.setTimeout(() => {
          if (hasAppliedDraftValues && draftFieldValues) {
            applyDraftValues();
          }
          syncDraftFileUploadNotes(formElement);
          syncFileUploadFileSizes(formElement);
          bindSingleFileUploadPreviews(formElement);
          syncCurrentPage(formElement);
          ensureAddressCountries(formRef.current, addressDefaults);
        }, 120);
      });
    };

    scheduleDraftSync();

    const observer = new MutationObserver(() => {
      scheduleDraftSync();
    });

    observer.observe(formElement, { childList: true, subtree: true });

    return () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      observer.disconnect();
    };
  }, [
    applyDraftValues,
    addressDefaults,
    bindSingleFileUploadPreviews,
    draftFieldValues,
    graphFormId,
    hasAppliedDraftValues,
    syncCurrentPage,
    syncDraftFileUploadNotes,
    syncFileUploadFileSizes,
  ]);

  const handleFormSubmitSuccess = useCallback(() => {
    markCurrentFormValuesAsSaved();
    setHasSubmittedForm(true);
    setDraftResumeUrl('');
    setDraftMessage('');
    setDraftError('');
    scrollFormWrapperToTop({ smooth: true });
  }, [markCurrentFormValuesAsSaved, scrollFormWrapperToTop]);
  const handleFormSubmitError = useCallback(
    (errorDetails) => {
      scrollFormWrapperToTop({ smooth: true });

      if (process.env.NODE_ENV !== 'production') {
        console.error('Gravity form submit error:', errorDetails);
      }
    },
    [scrollFormWrapperToTop]
  );

  const handleSaveAndContinue = async () => {
    if (!formRef.current || isSavingDraft) return;

    setIsSavingDraft(true);
    setDraftError('');
    setDraftMessage('');

    try {
      const values = formRef.current.getValues
        ? formRef.current.getValues()
        : {};
      const fieldValues = formatGravityFormPayload({
        serverData: formData?.gfForm?.formFields?.nodes || [],
        clientData: values,
      });

      const response = await saveGravityFormDraft({
        formId: formData?.gfForm?.databaseId || formId,
        fieldValues,
        entryMeta: {
          sourceUrl:
            typeof window !== 'undefined' ? window.location.href : undefined,
        },
        resumeToken: activeResumeToken || undefined,
      });

      if (response?.errors?.length || !response?.resumeUrl) {
        setDraftError(
          'Could not save your progress right now. Please try again in a moment.'
        );

        if (process.env.NODE_ENV !== 'production') {
          console.error('Save and continue failed:', response);
        }
        return;
      }

      setDraftResumeUrl(response.resumeUrl);
      if (response?.resumeToken) {
        setActiveResumeToken(response.resumeToken);
      }
      setDraftMessage('Resume link generated.');
      markCurrentFormValuesAsSaved();
    } catch (error) {
      setDraftError(
        'Could not save your progress right now. Please try again in a moment.'
      );

      if (process.env.NODE_ENV !== 'production') {
        console.error('Save and continue request error:', error);
      }
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleCopyResumeLink = async () => {
    if (!draftResumeUrl) return;

    try {
      if (!navigator?.clipboard?.writeText) {
        throw new Error('Clipboard API not available.');
      }

      await navigator.clipboard.writeText(draftResumeUrl);
      setDraftMessage('Link copied.');
    } catch (error) {
      setDraftMessage('Copy failed. Please copy the link manually.');

      if (process.env.NODE_ENV !== 'production') {
        console.error('Copy resume link failed:', error);
      }
    }
  };

  return {
    hasValidConfig,
    formData,
    formId,
    graphFormId,
    saveAndContinueSettings,
    hasSaveAndContinue,
    hasRequiredFields,
    presetValues,
    customFormFields,
    totalPages,
    currentPage,
    progressPercentage,
    shouldHoldFormRenderForRestore,
    isRestoringDraft,
    draftRestoreError,
    formRenderKey,
    setFormInstance,
    handleFormValidationCapture,
    handleFormSubmitSuccess,
    handleFormSubmitError,
    hasSubmittedForm,
    isSavingDraft,
    draftError,
    draftResumeUrl,
    draftMessage,
    handleSaveAndContinue,
    handleCopyResumeLink,
  };
}
